/* Squish Planet menu analytics v1: consent-aware, no navigation interception. */
(() => {
  'use strict';
  if (window.__spMenuAnalyticsV1) return;
  window.__spMenuAnalyticsV1 = true;
  const menuSelector = 'header-menu, header-drawer';
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  const drawerSections = {price: 'Shop by Price', shop: 'Shop by Brand',
    category: 'Shop by Theme', experience: 'Shop by Feel'};
  function label(el) {
    const title = el.querySelector('strong, .mega-menu__link-title, .menu-list__link-title');
    if (el.closest('header-drawer')) {
      const trigger = drawerSections[el.getAttribute('data-sp-tree-open')];
      if (trigger) return trigger;
      // Price cards need the full accessible label, not just their bold number.
      const accessible = el.getAttribute('aria-label');
      if (accessible && !el.matches('.sp-menu-v3__collection-card')) return clean(accessible);
      if (title) {
        const copy = title.cloneNode(true);
        copy.querySelectorAll('small, br').forEach(node => node.after(document.createTextNode(' ')));
        return clean(copy.textContent);
      }
      return clean(accessible || el.textContent);
    }
    return clean(title?.textContent || el.getAttribute('aria-label') || el.textContent);
  }
  function section(el) {
    if (el.closest('header-drawer')) {
      if (el.closest('.sp-menu-v3__collection-card')) return 'Featured collections';
      const panel = el.closest('[data-sp-tree-panel]');
      const tree = el.getAttribute('data-sp-tree-open') || panel?.getAttribute('data-sp-tree-panel');
      if (drawerSections[tree]) return drawerSections[tree];
      if (el.closest('.sp-menu-v3__image-help, .sp-menu-v3__support-panel')) return 'Help';
      if (el.closest('.sp-menu-v3__social')) return 'Social links';
    if ((el.matches('summary') && el.parentElement === el.closest('header-drawer')?.querySelector('details')) ||
        el.matches('summary[aria-label="Menu"], .sp-menu-v3__close, button[aria-label="Close"]')) return 'Menu controls';
      // Never borrow a heading from the drawer's shared section ancestor.
      return 'Main menu';
    }
    if (el.closest('.sp-menu-v3__collection-card')) return 'Featured collections';
    if (el.closest('[class*="feel"]')) return 'Shop by feel';
    const group = el.closest('li, [class*="section"], [class*="group"]');
    const heading = group?.querySelector('h2,h3,h4,.mega-menu__link--parent');
    return clean(heading?.textContent) || 'Main menu';
  }
  function interaction(el) {
    if (el.matches('a[href]')) return 'navigate';
    if (el.matches('[data-sp-tree-open]')) return 'expand';
    if (el.matches('[data-sp-tree-back]')) return 'back';
    if (el.matches('.sp-menu-v3__close, .menu-drawer__close-button, button[aria-label="Close"]')) return 'close';
    if (el.matches('summary')) {
      const details = el.closest('details');
      const root = el.closest('header-drawer')?.querySelector('details');
      return details === root ? (details?.open ? 'close' : 'open') :
        (details?.open ? 'collapse' : 'expand');
    }
    return 'control';
  }
  function metadata(el) {
    const drawer = el.closest('header-drawer');
    let path = '', linkUrl = '';
    if (el.matches('a[href]')) {
      try {
        const url = new URL(el.getAttribute('href'), location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        path = url.pathname; // Deliberately exclude query strings and fragments.
        linkUrl = url.origin + path; // Keep the actual external destination origin.
      } catch (_) { return null; }
    }
    const baseText = label(el);
    const state = interaction(el);
    // Use the already-registered Menu Item dimension for immediate report visibility.
    // The supplementary interaction parameter does not need a new definition to distinguish these rows.
    const text = drawer && el.matches('summary') && el.closest('details') ?
      clean(`${baseText} (${state === 'open' || state === 'expand' ? 'open' : 'close'})`) : baseText;
    if (!baseText) return null;
    return {menu_item: text, menu_section: section(el), menu_surface: drawer ? 'drawer' : 'desktop',
      menu_action: path ? 'navigate' : 'control', menu_interaction: state, link_text: text,
      link_url: linkUrl, destination_path: path};
  }
  function tag(el) {
    const meta = metadata(el);
    if (!meta) return;
    // Write only on change: rewriting identical attributes invalidated styles on every rescan.
    if (el.dataset.spMenuItem !== meta.menu_item) el.dataset.spMenuItem = meta.menu_item;
    if (el.dataset.spMenuSection !== meta.menu_section) el.dataset.spMenuSection = meta.menu_section;
    if (el.dataset.spMenuSurface !== meta.menu_surface) el.dataset.spMenuSurface = meta.menu_surface;
  }
  const itemSelector = 'a[href], button, summary';
  function scan(root) {
    // Same elements as before (links, buttons and summaries inside header-menu / header-drawer),
    // but queried from the menu roots only instead of the whole document.
    const roots = root.matches?.(menuSelector) ? [root] : root.querySelectorAll(menuSelector);
    roots.forEach(menu => menu.querySelectorAll(itemSelector).forEach(tag));
  }
  scan(document);
  // SP perf fix (2026-10-18): the observer used to run a full rescan in a microtask after EVERY DOM change
  // (countdowns, sliders, app widgets, every cart-drawer re-render), right on the critical path of a quick-add.
  // Rescans still follow DOM changes (menus can be filled in late), but cart-drawer re-renders are ignored,
  // rescans are coalesced to at most one per 1.5 s, and they run when the main thread is idle.
  const ignored = 'cart-drawer-component, fly-to-cart, .sp-cart-confetti';
  const idle = window.requestIdleCallback ? cb => requestIdleCallback(cb, {timeout: 2000}) : cb => setTimeout(cb, 300);
  let pending = false, lastScan = 0;
  new MutationObserver(records => {
    if (pending) return;
    if (records.every(r => r.target.nodeType === 1 && r.target.closest(ignored))) return;
    pending = true;
    setTimeout(() => idle(() => { pending = false; lastScan = Date.now(); scan(document); }),
      Math.max(0, 1500 - (Date.now() - lastScan)));
  }).observe(document.documentElement, {childList: true, subtree: true});
  let configured = false, configuredTag = null;
  function analyticsAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.analyticsProcessingAllowed === 'function' &&
        privacy.analyticsProcessingAllowed() === true;
    } catch (_) { return false; }
  }
  function ready() {
    if (!analyticsAllowed()) return false;
    if (typeof window.gtag !== 'function') return false;
    if (!configured || configuredTag !== window.gtag) {
      const tag = window.gtag;
      tag('config', 'GT-WVXMJP77', {send_page_view: false});
      configuredTag = tag;
      configured = true;
    }
    return true;
  }
  const inFlight = new Set();
  let probedTag = null, verifiedTag = null;
  function flush() {
    try {
      if (!ready()) return;
      const raw = JSON.parse(sessionStorage.getItem('sp_menu_click_queue_v1') || '[]');
      const queue = (Array.isArray(raw) ? raw : []).filter(item => item &&
        typeof item.id === 'string' && Number.isFinite(item.time) &&
        Date.now() - item.time >= 0 && Date.now() - item.time <= 600000 &&
        item.params?.menu_item && item.params.send_to === 'G-7PCJ6V5VWR').slice(-20);
      sessionStorage.setItem('sp_menu_click_queue_v1', JSON.stringify(queue));
      if (queue.length && verifiedTag !== window.gtag) {
        const tag = window.gtag;
        if (probedTag !== tag) {
          probedTag = tag;
          // Wait for the destination measurement tag, not merely an early gtag shim.
          // The returned value is never stored, logged or sent by this tracker.
          tag('get', 'G-7PCJ6V5VWR', 'client_id', value => {
            if (!value || window.gtag !== tag) return;
            verifiedTag = tag;
            flush();
          });
        }
        return false;
      }
      for (const item of queue) {
        if (inFlight.has(item.id)) continue;
        // At-most-once handoff: consume durably BEFORE dispatch. A tag callback
        // is not a delivery acknowledgment; never replay an uncertain send.
        const current = JSON.parse(sessionStorage.getItem('sp_menu_click_queue_v1') || '[]');
        sessionStorage.setItem('sp_menu_click_queue_v1', JSON.stringify(current.filter(x => x.id !== item.id)));
        inFlight.add(item.id);
        window.gtag('event', 'menu_click', {...item.params, event_id: item.id});
      }
      return true;
    } catch (_) { /* Malformed or unavailable storage never breaks the page. */ }
  }
  flush();
  let attempts = 0;
  const startup = setInterval(() => { if (flush() === true || ++attempts >= 20) clearInterval(startup); }, 500);
  const dismissals = new Map(); // Entries are removed on closure, cancellation or timeout.
  document.addEventListener('visitorConsentCollected', () => {
    if (analyticsAllowed()) flush();
    else {
      cancelAllDismissals();
      try { sessionStorage.removeItem('sp_menu_click_queue_v1'); } catch (_) {}
    }
  });
  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => { if (!error) flush(); });
  }
  function cancelAllDismissals() {
    for (const current of dismissals.values()) {
      current.observer.disconnect();
      clearTimeout(current.timer);
    }
    dismissals.clear();
  }
  function cancelDismissal(drawer) {
    const root = drawer?.querySelector('details');
    const current = root && dismissals.get(root);
    if (!current) return;
    current.observer.disconnect();
    clearTimeout(current.timer);
    dismissals.delete(root);
  }
  function watchDismissal(drawer, method) {
    const root = drawer?.querySelector('details');
    cancelDismissal(drawer); // The latest dismissal gesture owns the close attribution.
    if (!root?.open || !analyticsAllowed()) return;
    const observer = new MutationObserver(() => {
      if (root.open) return;
      observer.disconnect();
      clearTimeout(timer);
      dismissals.delete(root);
      try {
        if (!ready()) return;
        const item = method === 'escape' ? 'Close via Escape' : 'Close via backdrop';
        window.gtag('event', 'menu_click', {menu_item: item, menu_section: 'Menu controls',
          menu_surface: 'drawer', menu_action: 'control', menu_interaction: 'close',
          link_text: item, link_url: '', destination_path: '', send_to: 'G-7PCJ6V5VWR',
          page_location: location.origin + location.pathname, transport_type: 'beacon'});
      } catch (_) { /* Never affect native dismissal. */ }
    });
    const timer = setTimeout(() => { observer.disconnect(); dismissals.delete(root); }, 1500);
    dismissals.set(root, {observer, timer});
    observer.observe(root, {attributes: true, attributeFilter: ['open']});
  }
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || event.repeat) return;
    document.querySelectorAll('header-drawer').forEach(drawer => watchDismissal(drawer, 'escape'));
  }, true);
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const backdrop = event.target.closest('.menu-drawer__backdrop');
    if (backdrop) { watchDismissal(backdrop.closest('header-drawer'), 'backdrop'); return; }
    const el = event.target.closest('a[href], button, summary');
    if (!el || !el.closest(menuSelector)) return;
    cancelDismissal(el.closest('header-drawer'));
    if (el.matches('[disabled], [aria-disabled="true"]')) return;
    const meta = metadata(el);
    if (!meta) return;
    try {
      if (!ready()) return;
      const params = {...meta, send_to: 'G-7PCJ6V5VWR',
        page_location: location.origin + location.pathname, transport_type: 'beacon'};
      // Persist a consented same-tab navigation click until the destination page.
      // This avoids delaying navigation or losing the hit during page teardown.
      if (meta.menu_action === 'navigate' && !event.defaultPrevented && event.button === 0 &&
          !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey &&
          (!el.target || el.target === '_self') && !el.hasAttribute('download') &&
          new URL(el.href).origin === location.origin && new URL(el.href).pathname !== location.pathname) {
        try {
          const queue = JSON.parse(sessionStorage.getItem('sp_menu_click_queue_v1') || '[]');
          queue.push({id: crypto.randomUUID(), time: Date.now(), params});
          sessionStorage.setItem('sp_menu_click_queue_v1', JSON.stringify(queue.slice(-20)));
          return;
        } catch (_) { /* Storage unavailable: fall back to direct dispatch. */ }
      }
      window.gtag('event', 'menu_click', params);
    } catch (_) { /* Tracking must never break the menu. */ }
  }, true);
})();
